import { calculateDistanceBetweenCoords } from "@/utils/helpers";
import {
  buildColocationGroups,
  getColocatedCoordKey,
  getSpreadCoordinates,
} from "@/utils/placeMapMarkers";

export const getPlaceResultKeys = (place, fallbackId) => {
  const keys = [
    place?._id,
    place?.id,
    place?.placeUniqueNumber,
    place?.contentid,
    place?.contentId,
    place?.publicId,
    fallbackId,
  ];

  return keys.filter((value) => value != null && value !== "").map(String);
};

export const getPlaceResultId = (place, fallbackId) => {
  if (place?.source === "api") {
    return (
      place?.placeUniqueNumber ||
      place?.contentid ||
      place?.contentId ||
      place?.id ||
      place?._id ||
      place?.publicId ||
      fallbackId
    );
  }

  return getPlaceResultKeys(place, fallbackId)[0];
};

const normalizePlaceName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const getPlaceCoords = (place) => {
  const latitude = Number(place?.latitude);
  const longitude = Number(place?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

const isLikelySamePlace = (a, b) => {
  const aName = normalizePlaceName(a?.name);
  const bName = normalizePlaceName(b?.name);
  if (!aName || aName !== bName) return false;

  const aCoords = getPlaceCoords(a);
  const bCoords = getPlaceCoords(b);
  if (!aCoords || !bCoords) return false;

  return calculateDistanceBetweenCoords(aCoords, bCoords) <= 80;
};

const getPlaceSourcePriority = (place) => (place?.source === "api" ? 1 : 0);

const getPlaceCompletenessScore = (place) => {
  const mediaCount = Array.isArray(place?.placeMedia) ? place.placeMedia.length : 0;
  const reviewCount = Number(
    place?.totalDbReviews || place?.numberOfVisitorReviews || place?.totalReviews || 0
  );
  const hasPhone = place?.phoneNumber || place?.phone ? 1 : 0;
  const hasHours = place?.businessHours || place?.kakaoBusinessHours ? 1 : 0;
  return mediaCount * 10 + reviewCount + hasPhone + hasHours;
};

const shouldPreferPlace = (candidate, current) => {
  const candidatePriority = getPlaceSourcePriority(candidate);
  const currentPriority = getPlaceSourcePriority(current);
  if (candidatePriority !== currentPriority) {
    return candidatePriority < currentPriority;
  }

  return getPlaceCompletenessScore(candidate) > getPlaceCompletenessScore(current);
};

const mergeDuplicatePlace = (current, candidate) => {
  const preferred = shouldPreferPlace(candidate, current) ? candidate : current;
  const fallback = preferred === candidate ? current : candidate;
  const preferredMedia = Array.isArray(preferred?.placeMedia)
    ? preferred.placeMedia
    : [];
  const fallbackMedia = Array.isArray(fallback?.placeMedia)
    ? fallback.placeMedia
    : [];

  return {
    ...fallback,
    ...preferred,
    placeMedia: preferredMedia.length > 0 ? preferredMedia : fallbackMedia,
  };
};

export const mergePlacesByIdentifier = (...placeGroups) => {
  const seen = new Map();
  const merged = [];

  placeGroups.flat().forEach((place, index) => {
    if (!place) return;

    const keys = getPlaceResultKeys(place, `place-${index}`);
    const exactIndex = keys
      .map((key) => seen.get(key))
      .find((item) => item !== undefined);
    const duplicateIndex =
      exactIndex ??
      merged.findIndex((existingPlace) => isLikelySamePlace(existingPlace, place));

    if (duplicateIndex >= 0) {
      merged[duplicateIndex] = mergeDuplicatePlace(merged[duplicateIndex], place);
      getPlaceResultKeys(merged[duplicateIndex], `place-${duplicateIndex}`).forEach(
        (key) => seen.set(key, duplicateIndex)
      );
      keys.forEach((key) => seen.set(key, duplicateIndex));
      return;
    }

    const nextIndex = merged.length;
    merged.push(place);
    keys.forEach((key) => seen.set(key, nextIndex));
  });

  return merged;
};

export const getPlacesTotalCount = (response) => {
  const totalCount = Number(response?.totalCount);
  if (Number.isFinite(totalCount)) return totalCount;
  return Array.isArray(response?.places) ? response.places.length : 0;
};

export const addDistanceToPlaces = (places, loc, userLocation) => {
  return places?.map((place) => {
    const coords1 = {
      latitude: place?.latitude,
      longitude: place?.longitude,
    };

    const coords2 = {
      latitude: loc?.latitude || userLocation?.latitude,
      longitude: loc?.longitude || userLocation?.longitude,
    };

    return {
      ...place,
      distance:
        place?.distance || calculateDistanceBetweenCoords(coords1, coords2),
      distanceFromMe:
        place?.distance || calculateDistanceBetweenCoords(coords1, coords2),
    };
  });
};

// Co-located-pin fan-out helpers now live in @/utils/placeMapMarkers so the
// desktop and mobile marker builders share the exact same logic/constants (see
// that file's header + worklog-2026-07-27b). buildPlaceMapMarkers just consumes
// them below.
export const buildPlaceMapMarkers = (places = []) => {
  const colocationGroups = buildColocationGroups(places);
  const colocationSeen = new Map();

  return places.map((place, index) => {
    const placeIds = getPlaceResultKeys(place, `place-${index}`);

    const coordKey = getColocatedCoordKey(place);
    const groupSize = coordKey ? colocationGroups.get(coordKey) || 1 : 1;
    let coordinates = [Number(place?.longitude), Number(place?.latitude)];
    if (coordKey && groupSize > 1) {
      const memberIndex = colocationSeen.get(coordKey) || 0;
      colocationSeen.set(coordKey, memberIndex + 1);
      coordinates = getSpreadCoordinates(place, groupSize, memberIndex);
    }

    return {
      type: "Feature",
      id: getPlaceResultId(place, index),
      placeIds,
      index: index + 1,
      geometry: {
        type: "Point",
        coordinates,
      },
      properties: {
        placeIds,
        name: place?.name || place?.address,
        type: place?.type,
        description: place?.city || place?.state || place?.country,
        averageRating: place?.averageRating,
        totalReviews: place?.totalReviews,
        numberOfVisitorReviews: place?.numberOfVisitorReviews,
        naverRating: place?.naverRating,
        averageDbRating: place?.averageDbRating,
        totalDbReviews: place?.totalDbReviews,
      },
    };
  });
};
